import { APP_STYLE_CONFIG } from '@/constants/config';
import { ManagerDashboardFilterCards } from '../ManagerDashboardFilterCards';
import { ManagerDashboardProductSection } from '../ManagerDashboardProductSection';
import { Table } from '@/components/Tables';
import { TABLE_LABELS } from '@/constants';
import { useManagerDashboardFeature } from '@/features/manager-dashboard';
import React from 'react';

/** Props for the ManagerDashboardContent component */
export interface ManagerDashboardContentProps extends Omit<
  ReturnType<typeof useManagerDashboardFeature>,
  | 'currentUser'
  | 'handleModalUnlockCancel'
  | 'handleModalUnlockConfirm'
  | 'unlockModalError'
  | 'unlockModalOpen'
  | 'selectedUserForUnlock'
> {}

/**
 * ManagerDashboardContent Component
 *
 * Renders the main content area of the ManagerDashboard, including the product selector,
 * summary filter cards, and the team members data table.
 */
export const ManagerDashboardContent: React.FC<ManagerDashboardContentProps> = ({
  activeFilter,
  filteredResultsText,
  filteredUsers,
  handleFilterClear,
  handleFilterSelect,
  handleProductChange,
  handleSort,
  isLoading,
  managedUsers,
  managerActiveProduct,
  productOptions,
  showFilterResultsText,
  sortConfig,
  tableColumns,
  usersCertified,
  usersNeedingAttention,
  usersPassedPractice,
}) => (
  <div className="flex-1 space-y-8 overflow-auto px-0 py-6">
    {/* Product Section */}
    <ManagerDashboardProductSection
      activeProduct={managerActiveProduct}
      isLoading={isLoading}
      onProductChange={handleProductChange}
      productOptions={productOptions}
    />

    {/* Summary Filter Cards */}
    <ManagerDashboardFilterCards
      activeFilter={activeFilter}
      isDisabled={!managerActiveProduct}
      onSelect={handleFilterSelect}
      users={managedUsers.length}
      usersCertified={usersCertified.length}
      usersNeedingAttention={usersNeedingAttention.length}
      usersPassedPractice={usersPassedPractice.length}
    />

    {/* Data Table */}
    <Table
      animateRows={APP_STYLE_CONFIG.useAnimatedTables}
      columns={tableColumns}
      data={filteredUsers}
      filterResultsText={filteredResultsText}
      onFilterClear={handleFilterClear}
      onSort={handleSort}
      showFilterResultsText={showFilterResultsText}
      sortConfig={sortConfig}
      title={TABLE_LABELS.TEAM_MEMBERS}
    />
  </div>
);

ManagerDashboardContent.displayName = 'ManagerDashboardContent';

export default ManagerDashboardContent;
